import type { BillDeliverable, ExamPinCredential } from '@/types/receipt';
import type { BillPurchaseResult } from '@/types/bills';

/** Electricity prepaid token — groups digits every 4 (e.g. 7042-5289-5728). */
export function formatElectricityToken(token: string): string {
  if (!token) return '';
  const cleaned = token
    .replace(/^token\s*:\s*/i, '')
    .replace(/[\s-]/g, '');
  if (!cleaned) return token.trim();
  return cleaned.match(/.{1,4}/g)?.join('-') ?? cleaned;
}

export function formatReceiptCurrency(amount: number): string {
  return `₦${amount.toLocaleString('en-NG', { minimumFractionDigits: 0 })}`;
}

export function formatReceiptDateTime(date = new Date()) {
  const iso = date.toISOString();
  return {
    date: iso.slice(0, 10),
    time: date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }),
  };
}

export function formatExamPinCredential(entry: ExamPinCredential): string {
  if (entry.serial && entry.pin) return `Serial: ${entry.serial} · PIN: ${entry.pin}`;
  return entry.pin || entry.serial;
}

export function parseExamPurchasedCode(raw: string): ExamPinCredential[] {
  const text = raw.trim();
  if (!text) return [];

  return text
    .split('||')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const serialMatch = part.match(/Serial\s*No\s*:\s*([^,]+)/i);
      const pinMatch = part.match(/pin\s*:\s*(\S+)/i);
      if (serialMatch || pinMatch) {
        return {
          serial: serialMatch?.[1]?.trim() ?? '',
          pin: pinMatch?.[1]?.trim() ?? '',
        };
      }
      return { serial: '', pin: part };
    });
}

export function extractExamPins(data: BillPurchaseResult): ExamPinCredential[] {
  if (Array.isArray(data.examPins) && data.examPins.length > 0) {
    return data.examPins;
  }

  if (data.purchasedCode) {
    const parsed = parseExamPurchasedCode(String(data.purchasedCode));
    if (parsed.length > 0) return parsed.slice(0, 1);
  }

  if (!data.pins) return [];

  if (typeof data.pins === 'string') {
    return parseExamPurchasedCode(data.pins).slice(0, 1);
  }

  if (Array.isArray(data.pins)) {
    const first = data.pins[0];
    if (typeof first === 'string') {
      return parseExamPurchasedCode(first).slice(0, 1);
    }
    if (first && typeof first === 'object') {
      const obj = first as Record<string, unknown>;
      return [
        {
          serial: String(obj.Serial ?? obj.serial ?? '').trim(),
          pin: String(obj.Pin ?? obj.pin ?? '').trim(),
        },
      ];
    }
  }

  return [];
}

export function getElectricityDeliverable(
  data: BillPurchaseResult,
  meterType: 'prepaid' | 'postpaid',
): BillDeliverable | null {
  if (meterType === 'postpaid' || !data.token) return null;
  const raw = String(data.token);
  return {
    label: 'Electricity Token',
    rawValue: raw,
    value: formatElectricityToken(raw),
  };
}

export function getExamDeliverable(data: BillPurchaseResult): BillDeliverable | null {
  const pins = extractExamPins(data);
  const first = pins[0];
  if (!first) return null;
  const formatted = formatExamPinCredential(first);
  return {
    label: 'Exam PIN / Code',
    rawValue: first.pin || formatted,
    value: formatted,
  };
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
